"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  activeTheme: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("system");
  const [activeTheme, setActiveTheme] = useState<"light" | "dark">("light");

  const applyTheme = (t: Theme) => {
    let resolved: "light" | "dark" = "light";
    if (t === "dark") {
      resolved = "dark";
    } else if (t === "light") {
      resolved = "light";
    } else {
      resolved = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }

    const el = document.documentElement;
    el.setAttribute("data-theme", resolved);
    el.setAttribute("data-theme-pref", t);
    el.style.colorScheme = resolved;
    setActiveTheme(resolved);
  };

  useEffect(() => {
    const saved = (localStorage.getItem("shaaira-theme") as Theme) || "system";
    const valid: Theme = ["light", "dark", "system"].includes(saved)
      ? saved
      : "system";
    setThemeState(valid);
    applyTheme(valid);

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        applyTheme("system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("shaaira-theme", newTheme);
    applyTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, activeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
